import React, { useContext, useEffect, useMemo, useState } from "react";
import { ThemeContext } from "./contexts/ThemeContext";
import { NavbarContext } from "./contexts/NavbarContext";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { CONTACT, RDV, SERVICES } from "../config/routes";

import "./styles/Terms.css";

import termsBackgroundPng from "../images/png/background/terms_background.png";
import termsBackgroundPngMobile from "../images/png/background/terms_background_mobile.png";
import termsBackgroundAvif from "../images/avif/background/terms_background.avif";
import termsBackgroundAvifMobile from "../images/avif/background/terms_background_mobile.avif";
import termsBackgroundWebp from "../images/webp/background/terms_background.webp";
import termsBackgroundWebpMobile from "../images/webp/background/terms_background_mobile.webp";

const Terms = () => {
  const { t } = useTranslation();
  const { darkMode } = useContext(ThemeContext);
  const { navbarActive, setNavbarActive, scrollPosition } = useContext(NavbarContext);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 599);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 599);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const preventTouchMove = (e) => e.preventDefault();
    if (navbarActive && isMobile) {
      document.body.addEventListener("touchmove", preventTouchMove, { passive: false });
    } else {
      document.body.removeEventListener("touchmove", preventTouchMove);
    }
    return () => document.body.removeEventListener("touchmove", preventTouchMove);
  }, [navbarActive, isMobile]);

  useEffect(() => {
    const mainContainer = document.querySelector(".terms2026");
    if (!mainContainer) return;
    if (navbarActive) {
      mainContainer.classList.add("menu-open");
      document.body.classList.add("menu-open");
    } else {
      mainContainer.classList.remove("menu-open");
      document.body.classList.remove("menu-open");
    }
  }, [navbarActive]);

  useEffect(() => {
    if (!navbarActive) window.scrollTo(0, scrollPosition);
  }, [navbarActive, scrollPosition]);

  const handleMainContainerClick = () => {
    if (navbarActive && isMobile) setNavbarActive(false);
  };

  const metaTitle = t("terms.metaTitle");
  const metaDesc = t("terms.metaContent");

  const sections = useMemo(
    () => [
      { id: "intro", label: t("terms.dreamerAgencyPrivacy") },
      { id: "point1", label: t("terms.pointOne") },
      { id: "point2", label: t("terms.pointTwo") },
      { id: "point3", label: t("terms.pointThree") },
      { id: "point4", label: t("terms.pointFour") },
      { id: "point5", label: t("terms.pointFive") },
      { id: "point6", label: t("terms.pointSix") },
      { id: "point7", label: t("terms.pointSeven") },
    ],
    [t]
  );

  return (
    <main className={`terms2026 ${darkMode ? "dark" : "light"}`} onClick={handleMainContainerClick}>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <link rel="canonical" href="https://dreameragency.fr/terms" />

        <meta
          property="og:title"
          content="Conditions d'Utilisation de Dreamer Agency - Notre engagement envers la transparence"
        />
        <meta
          property="og:description"
          content="Découvrez les termes qui guident l'utilisation de nos services et de notre site web."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://dreameragency.fr/terms" />
        <meta property="og:image" content="https://dreameragency.fr/media/OG/terms_background.png" />
      </Helmet>

      <header className="terms2026-hero">
        <div className="terms2026-heroBg" aria-hidden="true">
          <picture>
            <source srcSet={termsBackgroundAvif} type="image/avif" media="(min-width: 600px)" />
            <source srcSet={termsBackgroundAvifMobile} type="image/avif" media="(max-width: 599px)" />
            <source srcSet={termsBackgroundWebp} type="image/webp" media="(min-width: 600px)" />
            <source srcSet={termsBackgroundWebpMobile} type="image/webp" media="(max-width: 599px)" />
            <source srcSet={termsBackgroundPng} media="(min-width: 600px)" />
            <source srcSet={termsBackgroundPngMobile} media="(max-width: 599px)" />
            <img src={termsBackgroundPng} alt="" />
          </picture>
        </div>
        <div className="terms2026-heroOverlay" aria-hidden="true" />

        <div className="terms2026-container terms2026-heroInner">
          <nav className="terms2026-breadcrumb" aria-label="Breadcrumb">
            <Link to="/" className="crumb">Accueil</Link>
            <span className="sep">/</span>
            <span className="crumbCurrent" aria-current="page">{t("terms.headerTitle")}</span>
          </nav>

          <h1 className="terms2026-h1">{t("terms.headerTitle")}</h1>
          <p className="terms2026-lead">{t("terms.headerParagraph")}</p>

          <div className="terms2026-heroActions">
            <Link to={RDV} className="btn btn-primary">Réserver un appel</Link>
            <Link to={CONTACT} className="btn btn-secondary">Contact</Link>
            <Link to={SERVICES} className="btn btn-ghost">Services</Link>
          </div>
        </div>
      </header>

      <section className="terms2026-section">
        <div className="terms2026-container terms2026-grid">
          <aside className="terms2026-side" aria-label="Sommaire">
            <div className="sideCard">
              <p className="sideTitle">Sommaire</p>
              <div className="toc">
                {sections.map((s) => (
                  <a key={s.id} href={`#${s.id}`} className="tocLink">
                    {s.label}
                  </a>
                ))}
              </div>
            </div>

            <div className="sideCard sideCardSoft">
              <p className="sideTitle">Besoin d’un avis ?</p>
              <p className="terms2026-muted">
                Pour toute question, contactez-nous. Pour aller vite : réservez un appel.
              </p>
              <Link to={CONTACT} className="btn btn-secondary btn-block">Contact</Link>
              <Link to={RDV} className="btn btn-primary btn-block">Réserver un appel</Link>
            </div>
          </aside>

          <article className="terms2026-content">
            <div className="terms2026-card" id="intro">
              <h2 className="terms2026-h2">{t("terms.dreamerAgencyPrivacy")}</h2>
              <p className="terms2026-muted">{t("terms.updateDate")}</p>
              <p className="terms2026-text">{t("terms.headerParagraph")}</p>
            </div>

            <div className="terms2026-card" id="point1">
              <h3 className="terms2026-h3">{t("terms.pointOne")}</h3>
              <p className="terms2026-text">{t("terms.pointOneParagraph")}</p>
            </div>

            <div className="terms2026-card" id="point2">
              <h3 className="terms2026-h3">{t("terms.pointTwo")}</h3>
              <p className="terms2026-text">{t("terms.pointTwoParagraph")}</p>
            </div>

            <div className="terms2026-card" id="point3">
              <h3 className="terms2026-h3">{t("terms.pointThree")}</h3>
              <p className="terms2026-text">{t("terms.pointThreeParagraph")}</p>
            </div>

            <div className="terms2026-card" id="point4">
              <h3 className="terms2026-h3">{t("terms.pointFour")}</h3>
              <p className="terms2026-text">{t("terms.pointFourParagraph")}</p>
            </div>

            <div className="terms2026-card" id="point5">
              <h3 className="terms2026-h3">{t("terms.pointFive")}</h3>
              <p className="terms2026-text">{t("terms.pointFiveParagraph")}</p>
            </div>

            <div className="terms2026-card" id="point6">
              <h3 className="terms2026-h3">{t("terms.pointSix")}</h3>
              <p className="terms2026-text">{t("terms.pointSixParagraph")}</p>
            </div>

            <div className="terms2026-card" id="point7">
              <h3 className="terms2026-h3">{t("terms.pointSeven")}</h3>
              <p className="terms2026-text">{t("terms.pointSevenParagraph")}</p>
              <a className="terms2026-mail" href={`mailto:${t("terms.ourEmail")}`}>
                {t("terms.ourEmail")}
              </a>
            </div>

            <div className="terms2026-cta">
              <div>
                <h2 className="terms2026-h2">On en parle ?</h2>
                <p className="terms2026-muted">
                  Si vous avez une question sur l’utilisation du site ou de nos services : contactez-nous ou réservez un appel.
                </p>
              </div>
              <div className="terms2026-ctaActions">
                <Link to={RDV} className="btn btn-primary">Réserver un appel</Link>
                <Link to={CONTACT} className="btn btn-ghost">Contact</Link>
              </div>
            </div>
          </article>
        </div>
      </section>
    </main>
  );
};

export default Terms;
